import React, { useState } from 'react';
import { X, Sparkles, Check, Zap, Key, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth, CHECKOUT_URL } from '../contexts/AuthContext';

/**
 * Upgrade prompt modal - shows when users hit free tier limits
 */
const UpgradeModal = () => {
  const {
    showUpgradeModal,
    upgradeReason,
    dismissUpgradeModal,
    openLicenseModal
  } = useAuth();

  if (!showUpgradeModal) return null;

  const proFeatures = [
    'Ability to save and load .cutcalc files',
    'Shop sheet with interactive cut checklist',
    'Print functionality',
    'Unlimited CSV/3D imports',
    'All optimization algorithms',
  ];

  const handleBuyNow = () => {
    window.open(CHECKOUT_URL, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#D66A28] p-6 text-white relative">
          <button
            onClick={dismissUpgradeModal}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Upgrade to Pro</h2>
          </div>
          <p className="text-white/90">{upgradeReason}</p>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Pro includes:</h3>
          <ul className="space-y-3 mb-6">
            {proFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-sage-600" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Buy Button */}
          <button
            onClick={handleBuyNow}
            className="w-full py-3 bg-[#D66A28] hover:bg-[#c25e23] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Zap className="w-5 h-5" />
            Buy Pro License
            <ExternalLink className="w-4 h-4" />
          </button>

          {/* Already have a license */}
          <button
            onClick={() => {
              dismissUpgradeModal();
              openLicenseModal();
            }}
            className="w-full py-2 mt-3 text-[#D66A28] hover:text-[#c25e23] text-sm font-medium flex items-center justify-center gap-2"
          >
            <Key className="w-4 h-4" />
            I already have a license key
          </button>

          <button
            onClick={dismissUpgradeModal}
            className="w-full py-2 mt-1 text-gray-400 hover:text-gray-600 text-sm"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * License key modal - for entering and managing license keys
 */
const LicenseKeyModal = () => {
  const {
    showLicenseModal,
    closeLicenseModal,
    license,
    isPro,
    activateLicense,
    deactivateLicense,
    licenseError,
  } = useAuth();

  const [licenseKey, setLicenseKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  if (!showLicenseModal) return null;

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      setLocalError('Please enter a license key');
      return;
    }

    setIsLoading(true);
    setLocalError('');

    const result = await activateLicense(licenseKey.trim());

    setIsLoading(false);

    if (result.success) {
      setLicenseKey('');
      closeLicenseModal();
    } else {
      setLocalError(result.error || 'Failed to activate license');
    }
  };

  const handleDeactivate = async () => {
    setIsLoading(true);
    await deactivateLicense();
    setIsLoading(false);
  };

  const errorMessage = localError || licenseError;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gray-900 p-6 text-white relative">
          <button
            onClick={closeLicenseModal}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Key className="w-7 h-7" />
            <h2 className="text-xl font-bold">License Key</h2>
          </div>
          <p className="text-white/70 text-sm">
            {isPro ? 'Manage your Pro license' : 'Enter your license key to unlock Pro features'}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {isPro && license ? (
            // Show current license
            <div>
              <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-sage-600" />
                  <span className="font-semibold text-sage-700">Pro License Active</span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <span className="text-gray-500">Key:</span>{' '}
                    {license.key.substring(0, 8)}...{license.key.substring(license.key.length - 4)}
                  </p>
                  {license.customerEmail && (
                    <p>
                      <span className="text-gray-500">Email:</span> {license.customerEmail}
                    </p>
                  )}
                  {license.activatedAt && (
                    <p>
                      <span className="text-gray-500">Activated:</span>{' '}
                      {new Date(license.activatedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleDeactivate}
                disabled={isLoading}
                className="w-full py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  'Deactivate License'
                )}
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">
                Deactivating allows you to use this license on another device
              </p>
            </div>
          ) : (
            // License entry form
            <form onSubmit={handleActivate}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                License Key
              </label>
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => {
                  setLicenseKey(e.target.value);
                  setLocalError('');
                }}
                placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                className={`w-full px-4 py-3 border rounded-xl mb-3 font-mono text-sm focus:outline-none focus:ring-2 ${
                  errorMessage
                    ? 'border-red-300 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-accent-200 focus:border-accent-400'
                }`}
                autoFocus
              />

              {errorMessage && (
                <p className="text-red-500 text-sm mb-3">{errorMessage}</p>
              )}

              <button
                type="submit"
                disabled={isLoading || !licenseKey.trim()}
                className="w-full py-3 bg-accent-400 hover:bg-accent-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Activate License
                  </>
                )}
              </button>

              <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500 mb-2">Don't have a license?</p>
                <button
                  type="button"
                  onClick={() => window.open(CHECKOUT_URL, '_blank')}
                  className="text-accent-500 hover:text-accent-600 text-sm font-medium flex items-center justify-center gap-1 mx-auto"
                >
                  Buy Pro License
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Pro badge - shows when user has Pro subscription
 */
const ProBadge = ({ small = false }) => {
  const { isPro } = useAuth();

  if (!isPro) return null;

  if (small) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-sage-100 text-sage-600 text-xs font-medium rounded">
        PRO
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-sage-500 text-white text-xs font-semibold rounded-full">
      <Sparkles className="w-3 h-3" />
      PRO
    </span>
  );
};

export {
  UpgradeModal,
  LicenseKeyModal,
  ProBadge,
};

export default UpgradeModal;
